<template>
    <button type="button" :class="classList" :disabled="disabled" @click="onSelected">
        <div ref="presentation" class="app-media-preview__presentation">
            <app-image
                v-if="media.url && media.is_image"
                :src="media.url"
                hide-fallback
                class="app-media-preview__image"
            />
            <span class="app-media-preview__icon">
                <v-icon color="background darken-4" size="3rem">{{ getIcon(media.icon) }}</v-icon>
            </span>
        </div>
        <div v-if="selectable" ref="checkbox" class="app-media-preview__checkbox">
            <v-checkbox v-if="!disabled" v-model="checked" color="primaryText" hide-details dense />
        </div>
        <div v-if="deletable" ref="actions" class="app-media-preview__actions">
            <app-button small color="background" icon="delete" :tooltip="$t('delete')" @click="onDelete" />
        </div>
        <div v-if="!hideInfo" ref="text" class="app-media-preview__text">
            <app-tooltip :value="tooltipText" :disabled="!tooltipText">
                <div class="app-media-preview__name clamp-1">{{ media.name }}</div>
            </app-tooltip>
            <div class="app-media-preview__date">{{ toDatetime(media.created_at) }}</div>
        </div>
    </button>
</template>

<script>
import { Media } from '@/models';

export default {
    name: 'AppMediaPreview',
    props: {
        media: {
            type: Media,
            required: true
        },
        selected: {
            type: Boolean,
            default: false
        },
        selectable: {
            type: Boolean,
            default: false
        },
        deletable: {
            type: Boolean,
            default: false
        },
        hideInfo: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        classList() {
            let classList = ['app-media-preview'];

            if (this.selected) {
                classList.push('app-media-preview--selected');
            }

            if (this.disabled) {
                classList.push('app-media-preview--disabled');
            }

            if (this.selectable) {
                classList.push('app-media-preview--selectable');
            }

            if (this.deletable) {
                classList.push('app-media-preview--deletable');
            }

            if (this.hideInfo) {
                classList.push('app-media-preview--hide-info');
            }

            return classList;
        },
        checked: {
            get() {
                return this.selected;
            },
            set() {
                //
            }
        },
        disabled() {
            if (!this._.get(this.media, 'file.id')) {
                return true;
            }
            return false;
        },
        tooltipText() {
            if (this.disabled) {
                return this.$t('there_is_no_file_attached_to_this_media_item');
            }
            return this.media.name || null;
        }
    },
    methods: {
        onSelected(e) {
            if (!this.selectable) {
                return false;
            }

            if ('path' in e && e.path.includes(this.$refs.actions)) {
                return;
            }
            this.$emit('selected', this.media.id);
        },
        onDelete() {
            if (!this.deletable) {
                return false;
            }

            this.$emit('delete', this.media.id);
        }
    }
};
</script>

<style lang="scss">
.app-media-preview {
    position: relative;
    background: rgba(0, 0, 0, 0.07);
    border: 1px solid rgba(0, 0, 0, 0);
    border-radius: 4px;
    overflow: hidden;
    width: 100%;
    max-width: 12rem;
    cursor: default;

    &.app-media-preview--selectable:not(.app-media-preview--disabled) {
        cursor: pointer;
    }

    &.app-media-preview--deletable,
    &.app-media-preview--selectable {
        &:not(.app-media-preview--disabled) {
            &:hover {
                .app-media-preview__presentation:after {
                    opacity: 1;
                    visibility: visible;
                }
                .app-media-preview__actions {
                    opacity: 1;
                    visibility: visible;
                    transform: translateY(0);
                }
            }
        }
    }

    &__presentation {
        position: relative;
        width: 100%;
        overflow: hidden;
        &:before {
            content: '';
            padding-bottom: 80%;
            display: block;
        }
        &:after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.3);
            border-radius: inherit;
            pointer-events: none;
            opacity: 0;
            visibility: hidden;
            transition: all 0.15s ease-in-out;
            z-index: 2;
        }
    }

    &__image {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        z-index: 2;
    }

    &__icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
    }

    &__text {
        background: var(--v-background-darken1);
        padding: 0.2rem 0.5rem;
        text-align: left;
    }

    &__name {
        font-size: 0.9rem;
        color: var(--v-primaryText-base);
    }

    &__date {
        font-size: 0.7rem;
        opacity: 0.7;
    }

    &__actions {
        position: absolute;
        top: 0;
        right: 0;
        padding: 0.2rem;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-100%);
        transition: all 0.15s ease-in-out;
        z-index: 3;
    }

    &__checkbox {
        position: absolute;
        top: 0;
        left: 0.2rem;
        z-index: 3;
    }

    &--selected {
        .app-media-preview {
            &__text {
                background: var(--v-primaryText-base);
                color: var(--v-background-base);
            }
            &__name {
                color: var(--v-background-base);
            }
        }
    }

    &--disabled {
        cursor: default;
        opacity: 0.6;
        .app-media-preview {
            &__name {
                color: inherit;
                opacity: 0.7;
            }
        }
    }
}
</style>
